/**
 * Control Plane — LINE OA Management
 *
 * LINE Login Config, Messaging API, OA Dashboard, Sparkline,
 * Rich Menu, Broadcast, and Audience management.
 */

import {
  CONTROL_PLANE_URL,
  cpGuestApiCall,
  getCPToken,
} from './controlPlaneClient'

/**
 * Multi-store scope: include the currently-selected storefront so accounts with
 * more than one store (e.g. CSIDE + Sub Scrub) manage the RIGHT LINE OA. Read
 * from the persisted auth store at call time to avoid a circular import; the
 * hooks add `activeStorefrontKey` to their queryKey so switching stores refetches.
 * The Control Plane validates the key belongs to the account before using it.
 */
export function activeStoreScope(): { storefront_key?: string } {
  try {
    const raw = localStorage.getItem('frappe-auth-storage')
    const key = raw ? (JSON.parse(raw)?.state?.activeStorefrontKey as string | null) : null
    return key ? { storefront_key: key } : {}
  } catch {
    return {}
  }
}

// === LINE Login Config API Types & Functions ===

export interface LineConfigResponse {
  line_login_enabled: boolean
  line_channel_id: string
  has_channel_secret: boolean
  callback_url: string
}

export interface LineConfigUpdate {
  line_login_enabled: boolean
  line_channel_id: string
  line_channel_secret?: string // empty = preserve existing
}

export interface LineConfigUpdateResponse {
  success: boolean
}

export async function cpGetLineConfig(storefrontKey?: string | null): Promise<LineConfigResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineConfigResponse>('control_plane.api.line_config.get_line_config', {
    method: 'POST',
    body: JSON.stringify({
      access_token: token, ...activeStoreScope(),
      ...(storefrontKey ? { storefront_key: storefrontKey } : {}),
    }),
  })
}

export async function cpUpdateLineConfig(
  data: LineConfigUpdate,
  storefrontKey?: string | null,
): Promise<LineConfigUpdateResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineConfigUpdateResponse>('control_plane.api.line_config.update_line_config', {
    method: 'POST',
    body: JSON.stringify({
      access_token: token, ...activeStoreScope(),
      ...data,
      ...(storefrontKey ? { storefront_key: storefrontKey } : {}),
    }),
  })
}

// === LINE Messaging API Types & Functions ===

export interface LineMessagingStatusResponse {
  connected: boolean
  bot_display_name: string
  bot_picture_url: string
  has_token: boolean
  channel_id?: string
  webhook_url?: string
  enable_line_order_confirmation: boolean
  enable_line_payment_confirmation: boolean
  enable_line_payment_reminder: boolean
  enable_line_shipment_notification: boolean
}

export interface LineNotificationPreferencesUpdate {
  enable_line_order_confirmation?: number
  enable_line_payment_confirmation?: number
  enable_line_payment_reminder?: number
  enable_line_shipment_notification?: number
}

export interface LineMessagingConnectResponse {
  success: boolean
  bot_display_name: string
  bot_picture_url: string
  chat_mode: string
  webhook_url?: string
}

export interface LineMessagingDisconnectResponse {
  success: boolean
}

export async function cpGetLineMessagingStatus(
  storefrontKey?: string | null
): Promise<LineMessagingStatusResponse> {
  const token = getCPToken()
  // An explicit storefrontKey (used to preview a specific store's OA logo) wins
  // over the globally-selected store.
  return cpGuestApiCall<LineMessagingStatusResponse>(
    'control_plane.api.line_messaging.get_line_messaging_status',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token,
        ...(storefrontKey ? { storefront_key: storefrontKey } : activeStoreScope()),
      }),
    }
  )
}

export async function cpConnectLineMessaging(messagingToken: string): Promise<LineMessagingConnectResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineMessagingConnectResponse>(
    'control_plane.api.line_messaging.connect_line_messaging',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), token: messagingToken }) }
  )
}

export async function cpConnectLineMessagingByCredentials(
  channelId: string,
  channelSecret: string
): Promise<LineMessagingConnectResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineMessagingConnectResponse>(
    'control_plane.api.line_messaging.connect_line_messaging_by_credentials',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token, ...activeStoreScope(),
        channel_id: channelId,
        channel_secret: channelSecret,
      }),
    }
  )
}

export async function cpDisconnectLineMessaging(): Promise<LineMessagingDisconnectResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineMessagingDisconnectResponse>(
    'control_plane.api.line_messaging.disconnect_line_messaging',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope() }) }
  )
}

export async function cpUpdateLineNotificationPreferences(
  prefs: LineNotificationPreferencesUpdate
): Promise<{ success: boolean }> {
  const token = getCPToken()
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.line_messaging.update_line_notification_preferences',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), ...prefs }) }
  )
}

// === LINE OA Dashboard API Types & Functions ===

export interface LineOADashboardResponse {
  followers: number
  targeted_reach: number | null
  new_this_month: number | null
  stats_available: boolean
}

export async function cpGetLineOADashboard(): Promise<LineOADashboardResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineOADashboardResponse>(
    'control_plane.api.line_messaging.get_line_oa_dashboard',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope() }) }
  )
}

// === LINE OA Sparkline API Types & Functions ===

export interface LineOASparklinePoint {
  date: string
  value: number
}

export interface LineOASparklineSummary {
  followers_total: number
  followers_growth: number
  followers_avg: number
  followers_prev_growth: number
  followers_delta: number
  followers_delta_pct: number | null
  reach_total: number
  reach_growth: number
  reach_avg: number
  reach_prev_growth: number
  reach_delta: number
  reach_delta_pct: number | null
  new_total: number
  new_avg: number
  new_prev_total: number
  new_delta: number
  new_delta_pct: number | null
}

export interface LineOASparklineResponse {
  followers: LineOASparklinePoint[]
  targeted_reach: LineOASparklinePoint[]
  new_followers: LineOASparklinePoint[]
  summary: LineOASparklineSummary
}

export async function cpGetLineOASparkline(
  period: '7d' | '30d' | '90d' | 'mtd' | 'ytd' = '30d'
): Promise<LineOASparklineResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineOASparklineResponse>(
    'control_plane.api.line_messaging.get_line_oa_sparkline',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), period }) }
  )
}

// === LINE OA Rich Menu API Types & Functions ===

export interface LineRichMenuArea {
  bounds: { x: number; y: number; width: number; height: number }
  action: { type: string; uri?: string; text?: string }
}

export interface LineRichMenu {
  richMenuId: string
  name: string
  chatBarText: string
  selected: boolean
  size: { width: number; height: number }
  areas: LineRichMenuArea[]
}

export interface LineRichMenuListResponse {
  menus: LineRichMenu[]
  default_rich_menu_id: string | null
}

export interface LineRichMenuCreateResponse {
  rich_menu_id: string
}

export async function cpGetLineOARichMenuList(): Promise<LineRichMenuListResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineRichMenuListResponse>(
    'control_plane.api.line_messaging.get_line_oa_richmenu_list',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope() }) }
  )
}

export async function cpCreateLineOARichMenu(menuData: object): Promise<LineRichMenuCreateResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineRichMenuCreateResponse>(
    'control_plane.api.line_messaging.create_line_oa_richmenu',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), menu_data: menuData }) }
  )
}

export async function cpUploadLineOARichMenuImage(
  richMenuId: string,
  file: File
): Promise<{ success: boolean }> {
  const token = getCPToken()
  const formData = new FormData()
  formData.append('access_token', token || '')
  formData.append('rich_menu_id', richMenuId)
  formData.append('image', file)

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.line_messaging.upload_line_oa_richmenu_image`,
    {
      method: 'POST',
      body: formData,
      credentials: 'omit',
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    let message = 'Image upload failed'
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages)
        let parsed = msgs[0]
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed) } catch { /* use as-is */ }
        }
        message = typeof parsed === 'object' && parsed?.message
          ? String(parsed.message)
          : String(parsed)
      } catch {
        message = String(error._server_messages)
      }
    } else if (error.message) {
      message = error.message
    }
    throw new Error(message)
  }

  const data = await response.json()
  return data.message as { success: boolean }
}

export async function cpDeleteLineOARichMenu(richMenuId: string): Promise<{ success: boolean }> {
  const token = getCPToken()
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.line_messaging.delete_line_oa_richmenu',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), rich_menu_id: richMenuId }) }
  )
}

export async function cpSetLineOARichMenuDefault(richMenuId: string): Promise<{ success: boolean }> {
  const token = getCPToken()
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.line_messaging.set_line_oa_richmenu_default',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), rich_menu_id: richMenuId }) }
  )
}

export async function cpClearLineOARichMenuDefault(): Promise<{ success: boolean }> {
  const token = getCPToken()
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.line_messaging.clear_line_oa_richmenu_default',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope() }) }
  )
}

// === LINE OA Broadcast API Types & Functions ===

export interface LineBroadcastLogEntry {
  message_type: 'text' | 'image' | 'image_text'
  content_preview: string | null
  image_url: string | null
  recipient_count: number
  sent_at: string
  line_request_id: string | null
  // Real LINE insight (populated once LINE has aggregated the broadcast)
  line_delivered?: number | null
  line_opens?: number | null
  line_unique_clicks?: number | null
  insight_updated?: string | null
}

export interface LineBroadcastHistoryResponse {
  broadcasts: LineBroadcastLogEntry[]
  total: number
  page: number
  page_size: number
}

export interface LineBroadcastQuotaResponse {
  quota_limit: number
  total_usage: number
  remaining: number | null
  quota_type: 'limited' | 'none'
}

export interface LineBroadcastSendResponse {
  success: boolean
  request_id: string
  broadcast_log_id: string
}

export async function cpGetLineOABroadcastQuota(): Promise<LineBroadcastQuotaResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineBroadcastQuotaResponse>(
    'control_plane.api.line_messaging.get_line_oa_broadcast_quota',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope() }) }
  )
}

export async function cpGetLineOABroadcastHistory(
  page: number = 1,
  pageSize: number = 20
): Promise<LineBroadcastHistoryResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineBroadcastHistoryResponse>(
    'control_plane.api.line_messaging.get_line_oa_broadcast_history',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), page, page_size: pageSize }) }
  )
}

/** Media + rich-message fields for a broadcast send (all optional, per type). */
export interface BroadcastMedia {
  imageFile?: File        // image / image_text hero
  videoFile?: File        // video (mp4) originalContentUrl
  previewFile?: File      // video previewImageUrl
  richImageFile?: File    // rich (Flex) hero image
  richLink?: string       // rich CTA destination (https)
  richLabel?: string      // rich CTA button label
  richTitle?: string      // rich headline
}

/** Parse a Frappe/CP error response body into a human message. */
async function parseCpFormError(response: Response, fallback: string): Promise<string> {
  const error = await response.json().catch(() => ({} as Record<string, unknown>))
  const serverMessages = (error as { _server_messages?: string })._server_messages
  if (serverMessages) {
    try {
      const msgs = JSON.parse(serverMessages)
      let parsed = msgs[0]
      if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed) } catch { /* as-is */ } }
      return typeof parsed === 'object' && parsed?.message ? String(parsed.message) : String(parsed)
    } catch { return String(serverMessages) }
  }
  const msg = (error as { message?: string }).message
  return msg ? String(msg) : fallback
}

/** Append the media/rich fields onto a broadcast FormData (order irrelevant). */
function appendBroadcastMedia(formData: FormData, media?: BroadcastMedia) {
  if (!media) return
  if (media.imageFile) formData.append('image', media.imageFile)
  if (media.videoFile) formData.append('video', media.videoFile)
  if (media.previewFile) formData.append('preview', media.previewFile)
  if (media.richImageFile) formData.append('rich_image', media.richImageFile)
  if (media.richLink) formData.append('rich_link', media.richLink)
  if (media.richLabel) formData.append('rich_label', media.richLabel)
  if (media.richTitle) formData.append('rich_title', media.richTitle)
}

export async function cpSendLineOABroadcast(
  messageType: string,
  textContent?: string,
  recipientCount?: number,
  media?: BroadcastMedia
): Promise<LineBroadcastSendResponse> {
  const formData = new FormData()
  formData.append('access_token', getCPToken() || '')
  const scope = activeStoreScope()
  if (scope.storefront_key) formData.append('storefront_key', scope.storefront_key)
  formData.append('message_type', messageType)
  if (textContent) formData.append('text_content', textContent)
  if (recipientCount != null) formData.append('recipient_count', String(recipientCount))
  appendBroadcastMedia(formData, media)

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.line_messaging.send_line_oa_broadcast`,
    { method: 'POST', body: formData, credentials: 'omit' }
  )
  if (!response.ok) throw new Error(await parseCpFormError(response, 'Broadcast failed'))
  const data = await response.json()
  return data.message || data
}

// === LINE OA Audience API Types & Functions ===

export interface LineFollowerProfile {
  user_id: string
  display_name: string | null
  picture_url: string | null
  status_message: string | null
  language: string | null
}

export interface LineAudienceListResponse {
  profiles: LineFollowerProfile[]
  next_token: string | null
  has_more: boolean
}

export async function cpGetLineOAAudienceList(
  nextToken?: string | null,
  pageSize?: number
): Promise<LineAudienceListResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineAudienceListResponse>(
    'control_plane.api.line_messaging.get_line_oa_audience_list',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token, ...activeStoreScope(),
        next_token: nextToken || null,
        page_size: pageSize || 20,
      }),
    }
  )
}

// === LINE Campaign API Types & Functions (segment + targeted send) ===

export type LineCampaignSegment =
  | 'awaiting_payment'
  | 'abandoned_cart'
  | 'paid'
  | 'all_customers'

export interface LineCampaignCartItem {
  name: string
  qty: number
}

export interface LineCampaignRecipient {
  line_user_id: string
  customer_name: string
  order_count: number
  total_spend: number
  unpaid_count: number
  cart_count: number
  last_activity: string
  /** Products inside the abandoned cart / unpaid order (abandoned_cart & awaiting_payment segments only). */
  items?: LineCampaignCartItem[]
  /** Total qty across all products in the cart / unpaid order. */
  item_count?: number
}

export interface LineCampaignSegmentResponse {
  segment: LineCampaignSegment
  total_matched: number
  reachable: number
  recipients: LineCampaignRecipient[]
}

export interface LineCampaignSendResponse {
  /** True ONLY when sent_count > 0 (at least one message reached LINE). */
  success: boolean
  sent_count: number
  skipped_count: number
  failed_count: number
  /** Human-readable explanation of the outcome (why nothing sent, or a summary). */
  reason?: string
  /** LINE's HTTP status + error body when LINE rejected the send. */
  line_error?: string
}

// Past TARGETED campaign sends (LINE Broadcast Log campaign_text/campaign_link).
// A campaign is a multicast — no broadcast request-id — so opens/clicks are NOT
// measurable and never returned. Delivered = recipient_count (count LINE accepted).
export interface LineCampaignHistoryEntry {
  name: string
  sent_at: string | null
  content_preview: string | null
  recipient_count: number
  message_type: 'campaign_text' | 'campaign_link' | 'campaign_rich' | 'campaign_video'
  /** Thumbnail for rich (hero) / video (preview) sends; null for text/link. */
  image_url?: string | null
  /** Coupon code stored at send time (explicit or parsed). null for pre-feature sends. */
  coupon_code?: string | null
  status: 'sent' | 'failed'
}

export interface LineCampaignHistoryResponse {
  history: LineCampaignHistoryEntry[]
}

// === Advanced audience filters (purchase-based, per-row AND/OR) ===

export type LineCampaignFilterConnector = 'and' | 'or'

export type LineCampaignFilterType =
  | 'bought_collection'
  | 'not_bought_collection'
  | 'bought_product'
  | 'not_bought_product'
  | 'spend_on_collection'
  | 'spend_on_product'
  | 'qty_of_collection'
  | 'qty_of_product'
  | 'total_spend'
  | 'order_count'
  | 'purchased_within'
  | 'not_purchased_within'

export interface LineCampaignFilterCondition {
  connector: LineCampaignFilterConnector
  type: LineCampaignFilterType
  item_groups?: string[]
  item_codes?: string[]
  amount?: number
  qty?: number
  days?: number
}

export interface LineCampaignFilterOptions {
  collections: string[]
  products: { item_code: string; item_name: string }[]
}

export async function cpGetLineCampaignSegment(params: {
  segment: LineCampaignSegment
  minOrders?: number
  minSpend?: number
  filters?: LineCampaignFilterCondition[]
}): Promise<LineCampaignSegmentResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineCampaignSegmentResponse>(
    'control_plane.api.line_messaging.line_campaign_segment',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token, ...activeStoreScope(),
        segment: params.segment,
        min_orders: params.minOrders ?? 0,
        min_spend: params.minSpend ?? 0,
        filters: JSON.stringify(params.filters ?? []),
      }),
    }
  )
}

export async function cpGetLineCampaignFilterOptions(): Promise<LineCampaignFilterOptions> {
  const token = getCPToken()
  return cpGuestApiCall<LineCampaignFilterOptions>(
    'control_plane.api.line_messaging.line_campaign_filter_options',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, ...activeStoreScope() }),
    }
  )
}

// Media for a rich (Flex) / video campaign send. Absent for text/link sends.
export interface CampaignMedia {
  videoFile?: File        // video (mp4) originalContentUrl
  previewFile?: File      // video previewImageUrl
  richImageFile?: File    // rich (Flex) hero image
  richLink?: string       // rich CTA destination (https)
  richLabel?: string      // rich CTA button label
  richTitle?: string      // rich headline
}

export async function cpSendLineCampaign(params: {
  lineUserIds: string[]
  textContent?: string
  includePaymentLink?: boolean
  messageType?: 'text' | 'rich' | 'video'
  media?: CampaignMedia
  couponCode?: string
}): Promise<LineCampaignSendResponse> {
  const token = getCPToken()
  const messageType = params.messageType ?? 'text'
  const couponCode = params.couponCode?.trim() || undefined

  // text/link: unchanged JSON passthrough (byte-identical to the legacy send).
  if (messageType === 'text') {
    return cpGuestApiCall<LineCampaignSendResponse>(
      'control_plane.api.line_messaging.line_campaign_send',
      {
        method: 'POST',
        body: JSON.stringify({
          access_token: token, ...activeStoreScope(),
          line_user_ids: JSON.stringify(params.lineUserIds),
          text_content: params.textContent ?? '',
          include_payment_link: params.includePaymentLink ? 1 : 0,
          message_type: 'text',
          ...(couponCode ? { coupon_code: couponCode } : {}),
        }),
      }
    )
  }

  // rich/video: multipart (media streamed to the CP proxy, then to the TS).
  const formData = new FormData()
  formData.append('access_token', token || '')
  const scope = activeStoreScope()
  if (scope.storefront_key) formData.append('storefront_key', scope.storefront_key)
  formData.append('line_user_ids', JSON.stringify(params.lineUserIds))
  formData.append('message_type', messageType)
  const media = params.media
  if (media?.videoFile) formData.append('video', media.videoFile)
  if (media?.previewFile) formData.append('preview', media.previewFile)
  if (media?.richImageFile) formData.append('rich_image', media.richImageFile)
  if (media?.richLink) formData.append('rich_link', media.richLink)
  if (media?.richLabel) formData.append('rich_label', media.richLabel)
  if (media?.richTitle) formData.append('rich_title', media.richTitle)
  if (couponCode) formData.append('coupon_code', couponCode)

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.line_messaging.line_campaign_send`,
    { method: 'POST', body: formData, credentials: 'omit' }
  )
  if (!response.ok) throw new Error(await parseCpFormError(response, 'Campaign send failed'))
  const data = await response.json()
  return data.message || data
}

export async function cpGetLineCampaignHistory(
  limit: number = 50
): Promise<LineCampaignHistoryResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineCampaignHistoryResponse>(
    'control_plane.api.line_messaging.line_campaign_history',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, ...activeStoreScope(), limit }),
    }
  )
}

// One recipient of a past campaign send. `blocked` is best-effort:
//   true  = the customer has blocked the OA (LINE get-profile 404)
//   false = reachable at check time
//   null  = unknown (transient error, or beyond the block-check cap)
export interface LineCampaignRecipientEntry {
  line_user_id: string
  name: string
  blocked: boolean | null
}

export interface LineCampaignRecipientsResponse {
  // recorded=false for sends made before recipient ids were stored (old rows).
  recorded: boolean
  recipients: LineCampaignRecipientEntry[]
  total: number
  capped: boolean // block-check only ran for the first `checked` recipients
  checked: number
}

export async function cpGetLineCampaignRecipients(
  broadcastLogName: string
): Promise<LineCampaignRecipientsResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineCampaignRecipientsResponse>(
    'control_plane.api.line_messaging.line_campaign_recipients',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token,
        ...activeStoreScope(),
        broadcast_log_name: broadcastLogName,
      }),
    }
  )
}

// Visit attribution for ONE past campaign send (PostHog, keyed by utm_campaign =
// the broadcast log name). Attribution is FUTURE-ONLY (older sends carry no
// utm_campaign tag → visits 0) and LINK-REQUIRED (a send with no URL drives no
// tagged visit). A value of -1 means PostHog was unreachable at query time;
// configured=false means analytics isn't set up.
export interface LineCampaignVisitsResponse {
  configured: boolean
  campaign: string
  visits: number // total pageviews via this campaign's tagged links (-1 = unavailable)
  unique_visitors: number // distinct visitors (-1 = unavailable)
  orders: number // best-effort attributed orders (-1 = unavailable, may under-count)
}

export async function cpGetLineCampaignVisits(
  broadcastLogName: string
): Promise<LineCampaignVisitsResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineCampaignVisitsResponse>(
    'control_plane.api.line_messaging.line_campaign_visits',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token,
        ...activeStoreScope(),
        broadcast_log_name: broadcastLogName,
      }),
    }
  )
}

// === LINE Scheduled Broadcasts ===

export interface LineScheduledBroadcast {
  name: string
  message_type: 'text' | 'image' | 'image_text'
  content_preview: string | null
  image_url: string | null
  recipient_count: number
  scheduled_at: string
}

export interface LineScheduledListResponse {
  scheduled: LineScheduledBroadcast[]
}

/** Schedule a broadcast for a future time. scheduledAt = 'YYYY-MM-DD HH:MM:SS' (Asia/Bangkok). */
export async function cpScheduleLineOABroadcast(
  messageType: string,
  scheduledAt: string,
  textContent?: string,
  imageFile?: File,
  recipientCount?: number
): Promise<{ success: boolean; scheduled_broadcast_id: string; scheduled_at: string }> {
  const token = getCPToken()
  const formData = new FormData()
  formData.append('access_token', token || '')
  const scope = activeStoreScope()
  if (scope.storefront_key) formData.append('storefront_key', scope.storefront_key)
  formData.append('message_type', messageType)
  formData.append('scheduled_at', scheduledAt)
  if (textContent) formData.append('text_content', textContent)
  if (recipientCount != null) formData.append('recipient_count', String(recipientCount))
  if (imageFile) formData.append('image', imageFile)

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.line_messaging.schedule_line_oa_broadcast`,
    { method: 'POST', body: formData, credentials: 'omit' }
  )
  if (!response.ok) throw new Error(await parseCpFormError(response, 'Could not schedule broadcast'))
  const data = await response.json()
  return data.message || data
}

export async function cpGetLineOABroadcastScheduled(): Promise<LineScheduledListResponse> {
  const token = getCPToken()
  return cpGuestApiCall<LineScheduledListResponse>(
    'control_plane.api.line_messaging.get_line_oa_broadcast_scheduled',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope() }) }
  )
}

export async function cpCancelLineOABroadcast(name: string): Promise<{ success: boolean }> {
  const token = getCPToken()
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.line_messaging.cancel_line_oa_broadcast',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope(), name }) }
  )
}

// === LINE Automated Notification Activity ===

export interface LineNotificationRecent {
  customer_name: string
  reference_name: string | null
  sent_at: string | null
  status: string
}
export interface LineNotificationTypeActivity {
  last_sent: string | null
  today: number
  month: number
  total: number
  recent: LineNotificationRecent[]
}
export type LineNotificationActivity = Record<string, LineNotificationTypeActivity>

export async function cpGetLineNotificationActivity(): Promise<LineNotificationActivity> {
  const token = getCPToken()
  return cpGuestApiCall<LineNotificationActivity>(
    'control_plane.api.line_messaging.get_line_notification_activity',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...activeStoreScope() }) }
  )
}
